import React, { useEffect, useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { signIn, signUp, autoSignIn } from "aws-amplify/auth";
import { isValidEmail, isValidPassword, toE164Kr } from "../helpers/utils";
import { LOG_IN_COOKIE, REST_API_URL } from "../constants";
import { Marketing, Privacy, TermsOfUse } from "../helpers/policies";
import { modal } from "../modal/ModalService";
import { useLang } from "../context/LangProvider";
import LanguageDropdown from "../helpers/LanguageDropdown";
import API from "../service/API";
import CountryDropDown from "../components/CountryDropDown";
import { deleteCookie } from "../helpers/cookie";
import { ko } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ko", ko);

const BENEFITS = [
  {
    key: "coupon",
    label: "신규 가입 축하 쿠폰 5,000원",
  },
  {
    key: "ship",
    label: "그랑핸드 회원은 언제나 무료 배송",
  },
];

const terms = [
  {
    key: "tos",
    title: "서비스 이용 약관 동의",
    required: true,
    body: <TermsOfUse />,
  },
  {
    key: "privacy",
    title: "개인정보 수집 및 이용 동의",
    required: true,
    body: <Privacy />,
  },
  {
    key: "marketing",
    title: "개인정보 수집 및 이용 및 이벤트/마케팅 수신 동의",
    required: false,
    body: <Marketing />,
  },
];

const terms_nobody = [
  {
    key: "age",
    title: "만 14세 이상입니다.",
    required: true,
    body: "", // 본문 없음(체크만)
    noBody: true,
  },
];

const StepRender = React.memo(function StepRender({
  step,
  onNext,
  checked,
  toggle,
  allOk,
  setAll,
  requiredOk,
  email,
  setEmail,
  birth,
  setBirth,
  password,
  onAuthroize,
  setPassword,
  rePassword,
  setRepassword,
  onSignin,
  existing,
  i18n,
  phone,
  setPhone,
  phoneAuthCode,
  setPhoneAuthCode,
  onConfrimPhoneAuthCode,
  onSendPhoneAuthCode,
  lang,
  onCountrySelect,
  countryCode,
  veifyCodeSent,
  setCodeSent,
  isSending,
  onForeignerChange,
  foreigner,
  isLoading,
}) {
  switch (step) {
    case 1:
      return (
        <section className="terms__wrap">
          <header className="terms__head">
            <h2 className="terms__title">{i18n("signup.step1.title")}</h2>
            <p className="terms__subtitle">{i18n("signup.step1.subtitle")}</p>
          </header>
          <form>
            <div className="terms__cards">
              {terms.map((t) => (
                <article className="terms-card" key={t.key}>
                  <label className="terms-card__head">
                    <input
                      type="checkbox"
                      checked={checked[t.key]}
                      onChange={() => toggle(t.key)}
                    />
                    <div className="terms-card__title">
                      {t.required ? (
                        <em className="req">{i18n("common.requiredTag")}</em>
                      ) : (
                        <em className="opt">{i18n("common.optionalTag")}</em>
                      )}
                      <div className="h6">
                        {i18n(`signup.terms.${t.key}.title`)}
                      </div>
                    </div>
                  </label>
                  <div className="terms-card__body" aria-live="polite">
                    <div className="terms-card__scroll">
                      {t.body}
                      <br />
                      <br />
                      {t.body}
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {terms_nobody.map((t) => (
              <article className="terms-card narrow" key={t.key}>
                <label className="terms-card__head">
                  <input
                    type="checkbox"
                    checked={checked[t.key]}
                    onChange={() => toggle(t.key)}
                  />
                  <div className="terms-card__title">
                    {t.required ? (
                      <em className="req">{i18n("common.requiredTag")}</em>
                    ) : (
                      <em className="opt">{i18n("common.optionalTag")}</em>
                    )}
                    <div className="h6">
                      {i18n(`signup.terms.${t.key}.title`)}
                    </div>
                  </div>
                </label>
              </article>
            ))}

            <div className="dashed-divider" />

            {/* 전체 동의 */}
            <label className="terms__agree-all">
              <input
                type="checkbox"
                checked={allOk}
                onChange={(e) => setAll(e.target.checked)}
              />
              <div className="h6">
                <strong>GRANHAND</strong> {i18n("signup.terms.agreeAll")}
              </div>
            </label>

            {/* 버튼 */}
            <button
              type="button"
              onClick={onNext}
              className="login__btn login__btn--outline"
              disabled={!requiredOk}
            >
              {i18n("signup.terms.next")}
            </button>
          </form>
        </section>
      );

    case 2:
      return (
        <div className="signup-2">
          <div className="progress-bar">
            <div
              className="progress"
              style={{ width: "33.33%", position: "absolute", top: 0, left: 0 }}
            />
          </div>
          <div className="h5">{i18n("signup.step2.subtitle")}</div>

          <form className="login__form" style={{ marginTop: "30px" }}>
            {/* 아이디 */}
            <div className="login__label h6" htmlFor="email">
              {i18n("signup.form.email.label")}
            </div>
            <input
              id="email"
              type="email"
              className="login__input"
              placeholder={i18n("signup.form.email.placeholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <div className="login__label h6" htmlFor="birth">
              {i18n("signup.form.birth.label")}
            </div>
            <DatePicker
              className="login__input"
              selected={birth}
              locale="ko"
              placeholderText={i18n("signup.form.birth.placeholder")}
              onChange={(date) => {
                if (!date) {
                  setBirth("");
                  return;
                }
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, "0");
                const dd = String(date.getDate()).padStart(2, "0");
                setBirth(`${yyyy}-${mm}-${dd}`);
              }}
              minDate={new Date(1900, 0, 1)}
              maxDate={new Date()}
              openToDate={new Date(1990, 0, 1)}
              dateFormat="yyyy-MM-dd"
              showYearDropdown
              showMonthDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={120}
              onChangeRaw={(e) => e.preventDefault()}
              onKeyDown={(e) => e.preventDefault()}
              withPortal
              portalId="birth-date-portal"
              onFocus={(e) => e.target.blur()} 
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                decreaseYear,
                increaseYear,
              }) => {
                const year = date.getFullYear();
                const month = date.getMonth() + 1;

                return (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                    }}
                  >
                    <button type="button" onClick={decreaseYear}>
                      «
                    </button>
                    <button type="button" onClick={decreaseMonth}>
                      ‹
                    </button>
                    <span>
                      {year}년 {month}월
                    </span>{" "}
                    {/* 👈 한국식 순서 */}
                    <button type="button" onClick={increaseMonth}>
                      ›
                    </button>
                    <button type="button" onClick={increaseYear}>
                      »
                    </button>
                  </div>
                );
              }}
            />

            <button
              type="button"
              onClick={onNext}
              disabled={!isValidEmail(email) || !birth}
              aria-disabled={!isValidEmail(email) || !birth}
              className="login__btn login__btn--primary"
              style={{ marginTop: "80px" }}
            >
              {i18n("common.next")}
            </button>
          </form>
        </div>
      );

    case 3:
      return (
        <div className="signup-2">
          <div className="progress-bar">
            <div
              className="progress"
              style={{
                width: "33.33%",
                position: "absolute",
                top: 0,
                left: "33.33%",
              }}
            />
          </div>
          <div className="h5">{i18n("signup.step3.subtitle")}</div>
          <div className="h6">{i18n("signup.step3.description")}</div>

          <form className="login__form" style={{ marginTop: "30px" }}>
            <div className="login__label h6" htmlFor="password">
              {i18n("signup.form.password.label")}
            </div>
            <input
              id="password"
              type="password"
              className="login__input"
              placeholder={i18n("signup.form.password.placeholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />

            <input
              id="rePassword"
              type="password"
              className="login__input"
              placeholder={i18n("signup.form.passwordConfirm.placeholder")}
              value={rePassword}
              onChange={(e) => setRepassword(e.target.value)}
              style={{ marginTop: "15px" }}
              autoComplete="current-password"
            />

            <button
              type="button"
              onClick={onNext}
              disabled={!isValidPassword(password) || password !== rePassword}
              aria-disabled={
                !isValidPassword(password) || password !== rePassword
              }
              className="login__btn login__btn--primary"
              style={{ marginTop: "80px" }}
            >
              {i18n("common.next")}
            </button>
          </form>
        </div>
      );

    case 4:
      return lang === "ko" ? (
        <div className="signup-2">
          <div className="progress-bar">
            <div
              className="progress"
              style={{
                width: "33.33%",
                position: "absolute",
                top: 0,
                left: "66.66%",
              }}
            />
          </div>

          <div className="h4 bold">{i18n("signup.step4.title")}</div>
          <p>{i18n("signup.step4.desc")}</p>

          <button
            type="button"
            onClick={onAuthroize}
            className="login__btn login__btn--primary"
            style={{ marginTop: "80px" }}
          >
            {i18n("signup.step4.cta")}
          </button>
        </div>
      ) : (
        <div className="signup-2">
          <div className="progress-bar">
            <div
              className="progress"
              style={{
                width: "33.33%",
                position: "absolute",
                top: 0,
                left: "66.66%",
              }}
            />
          </div>
          <div className="flex-column" style={{ gap: "15px" }}>
            <div className="h4 bold">Please verify your mobile phone.</div>
            <CountryDropDown onCountrySelect={onCountrySelect} />
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {countryCode ? `+${countryCode}` : null}
              <input
                id="phone"
                type="text"
                pattern="[0-9]*"
                className="login__input"
                style={{ flex: 1 }}
                placeholder="Enter phone number (numbers only)"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setCodeSent(false);
                }}
              />
            </div>
            <button
              type="button"
              onClick={onSendPhoneAuthCode}
              disabled={!phone || isSending}
              aria-disabled={!phone || isSending}
              className="login__btn login__btn--primary"
            >
              Request verification code
            </button>
          </div>
          <div
            className="flex-column"
            style={{ gap: "15px", marginTop: "50px" }}
          >
            <div>
              <div className="login__label h6">Verification code</div>
              <input
                id="phoneAuthCode"
                type="text"
                className="login__input"
                placeholder="6-digit"
                value={phoneAuthCode}
                onChange={(e) => setPhoneAuthCode(e.target.value)}
              />
            </div>
            {veifyCodeSent ? (
              <form className="login__form" style={{ marginTop: "30px" }}>
                <div className="login__label h6" htmlFor="email">
                  Name
                </div>
                <input
                  id="name"
                  type="name"
                  className="login__input"
                  placeholder={"Enter your name"}
                  value={foreigner.name}
                  onChange={(e) => onForeignerChange(e.target.value, "name")}
                />
                <div className="login__label h6" htmlFor="birth">
                  Gender
                </div>
                <select
                  onChange={(e) => onForeignerChange(e.target.value, "gender")}
                  id="gender"
                  name="gender"
                  class="login__dropdown"
                  value={foreigner.gender}
                >
                  <option value="" disabled selected>
                    Select gender
                  </option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </form>
            ) : null}
            <button
              type="button"
              disabled={
                !phone ||
                !phoneAuthCode ||
                !veifyCodeSent ||
                isLoading ||
                !foreigner.name ||
                !foreigner.gender
              }
              aria-disabled={
                !phone ||
                !phoneAuthCode ||
                !veifyCodeSent ||
                isLoading ||
                !foreigner.name ||
                !foreigner.gender
              }
              onClick={onConfrimPhoneAuthCode}
              className="login__btn login__btn--primary"
            >
              Sign up
            </button>
          </div>
        </div>
      );

    case 5:
      return existing ? (
        <div className="signup-5">
          <div className="h4 bold">{i18n("signup.existing.title")}</div>
          <p>
            {i18n("signup.existing.desc.1")}
            <br />
            {i18n("signup.existing.desc.2")}
          </p>
          <button
            type="button"
            onClick={onSignin}
            className="login__btn login__btn--outline"
            style={{ marginTop: "80px" }}
          >
            {i18n("signin.cta.login")}
          </button>
        </div>
      ) : (
        <div className="signup-5">
          <div
            className="h3 bold"
            style={{
              textAlign: "center",
              color: "#6f6963",
              fontSize: "20px",
            }}
          >
            {i18n("signup.complete.title")}
          </div>

          <div className="benefit-wrapper">
            {BENEFITS.map((b) => (
              <div key={b.key} className="benefit-line">
                <img
                  alt={i18n("signup.benefit.iconAlt")}
                  src={require(`../img/icon/${b.key}.png`)}
                  className="icon"
                />
                {i18n(`signup.benefit.${b.key}`)}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={onSignin}
            className="login__btn login__btn--outline"
            style={{ marginTop: "80px" }}
          >
            {i18n("signup.complete.backToShop")}
          </button>
        </div>
      );
    default:
      return null;
  }
});

const TopBar = ({ onBack, i18n, step }) => {
  return (
    <header
      className="topbar"
      style={{
        maxWidth: step === 1 ? "740px" : "380px",
        margin: "0 auto",
        padding: step === 1 ? "0 16px" : "0 10px",
      }}
    >
      <div className="topbar__inner">
        <button
          type="button"
          className="topbar__back"
          onClick={onBack}
          style={{ marginBottom: 0 }}
          aria-label={i18n("signin.nav.back.aria")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="topbar__back-text h6">
            {step === 1 ? i18n("signin.nav.back") : i18n("common.prevStep")}
          </div>
        </button>
        {step === 1 ? <LanguageDropdown /> : null}
      </div>
    </header>
  );
};

export default function SignupPage() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const { i18n, lang } = useLang();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [birth, setBirth] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneAuthCode, setPhoneAuthCode] = useState("");
  const [password, setPassword] = useState("");
  const [rePassword, setRepassword] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [isSending, setSending] = useState(false);
  const [veifyCodeSent, setCodeSent] = useState(false);
  const [checked, setChecked] = useState(() =>
    Object.fromEntries(terms.map((t) => [t.key, false]))
  );
  const [existing, setExisting] = useState(null);
  const [foreigner, setForeigner] = useState({});
  const [isLoading, setLoading] = useState(false);

  const doSignUp = useCallback(
    async (data) => {
      try {
        const attrs = {
          phone_number: toE164Kr(data.phone),
          name: data.name,
          birthdate: data?.birth || birth,
          gender: data.gender || null,
          "custom:createdAt": new Date().toISOString(),
          "custom:marketing": checked.marketing ? "true" : "false",
        };
        setLoading(true);
        const rst = await signUp({
          username: email.trim(),
          password,
          options: { userAttributes: attrs, autoSignIn: true },
        });
        if (
          rst.isSignUpComplete &&
          rst.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN"
        ) {
          try {
            await autoSignIn();
          } catch (e) {
            try {
              await signIn({ username: email.trim(), password });
            } catch (err) {
              console.log("Failed to siginin", err);
            }
          }
        }
        setStep(5);
        setLoading(false);
      } catch (err) {
        if (err.code === "UsernameExistsException") {
          setExisting(true);
        } else {
          modal.alert({
            message: i18n("signup.error.fail"),
            okText: i18n("common.ok"),
            backdropClose: true,
          });
        }
      }
    },
    [birth, checked.marketing, email, password, i18n]
  );

  const doSignUpRef = useRef(doSignUp);
  useEffect(() => {
    doSignUpRef.current = doSignUp;
  }, [doSignUp]);

  const requiredOk = [...terms, ...terms_nobody]
    .filter((t) => t.required)
    .every((t) => checked[t.key]);
  const allOk = terms.every((t) => checked[t.key]);
  const toggle = (key) =>
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));

  const setAll = (val) =>
    setChecked(
      Object.fromEntries([...terms, ...terms_nobody].map((t) => [t.key, val]))
    );

  const handleBackPress = () => {
    if (step === 1) {
      nav("signin");
    } else {
      setStep(step - 1);
      setCodeSent(false);
    }
  };

  const handleSignin = async () => {
    deleteCookie(LOG_IN_COOKIE);
    await refresh();
    nav("/");
  };

  const handleConfrmPhoneAuthCode = async () => {
    try {
      const isValid = await API.confirmPhoneAuthCode(
        phoneAuthCode,
        phone,
        countryCode
      );
      if (isValid) {
        doForeignerSignup();
      } else {
        modal.alert({
          message: "The verification code is incorrect.",
          okText: "OK",
          backdropClose: true,
        });
      }
    } catch (err) {
      modal.alert({
        message: "Something wrong... Please try again.",
        okText: "OK",
        backdropClose: true,
      });
    }
  };

  const doForeignerSignup = async () => {
    try {
      const attrs = {
        phone_number: `+${countryCode}${phone}`,
        name: foreigner.name,
        birthdate: birth,
        gender: foreigner.gender || null,
        "custom:createdAt": new Date().toISOString(),
        "custom:marketing": checked.marketing ? "true" : "false",
      };

      const rst = await signUp({
        username: email.trim(),
        password,
        options: { userAttributes: attrs, autoSignIn: true },
      });
      if (
        rst.isSignUpComplete &&
        rst.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN"
      ) {
        try {
          await autoSignIn();
        } catch (e) {
          try {
            await signIn({ username: email.trim(), password });
          } catch (err) {
            console.log("Failed to siginin", err);
          }
        }
      }
      setStep(5);
    } catch (err) {
      if (err.code === "UsernameExistsException") {
        setExisting(true);
      } else {
        modal.alert({
          message: "회원가입에 실패했습니다.",
          okText: "확인",
          backdropClose: true,
        });
      }
    }
  };

  const handleForeignerChange = (value, type) => {
    setForeigner({ ...foreigner, [type]: value });
  };

  const handleSendPhoneAuthCode = async () => {
    try {
      setSending(true);
      await API.sendPhoneAuthCode(phone, countryCode);
      setCodeSent(true);
      setSending(false);
    } catch (err) {
      setSending(false);
    }
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleAuthorize = async () => {
    window.MOBILEOK?.process(`${REST_API_URL}/mok/request`, "WB", "result");
  };

  const handleCountrySelect = (country) => {
    setCountryCode(country.dialCode);
  };

  useEffect(() => {
    window.result = async function (payload) {
      fetch(
        `${REST_API_URL}/mok/result?txId=${encodeURIComponent(
          JSON.parse(payload).txId
        )}`
      )
        .then((r) => r.json())
        .then((json) => {
          doSignUpRef.current(json);
        });
    };

    return () => {
      delete window.result;
    };
  }, []);

  return (
    <main className="signup">
      <TopBar onBack={handleBackPress} step={step} i18n={i18n} />
      <StepRender
        step={step}
        onNext={handleNext}
        checked={checked}
        toggle={toggle}
        allOk={allOk}
        setAll={setAll}
        requiredOk={requiredOk}
        email={email}
        setEmail={setEmail}
        birth={birth}
        setBirth={setBirth}
        password={password}
        setPassword={setPassword}
        rePassword={rePassword}
        setRepassword={setRepassword}
        onAuthroize={handleAuthorize}
        onSignin={handleSignin}
        existing={existing}
        i18n={i18n}
        phone={phone}
        setPhone={setPhone}
        phoneAuthCode={phoneAuthCode}
        setPhoneAuthCode={setPhoneAuthCode}
        onSendPhoneAuthCode={handleSendPhoneAuthCode}
        onConfrimPhoneAuthCode={handleConfrmPhoneAuthCode}
        lang={lang}
        onCountrySelect={handleCountrySelect}
        countryCode={countryCode}
        veifyCodeSent={veifyCodeSent}
        setCodeSent={setCodeSent}
        isSending={isSending}
        onForeignerChange={handleForeignerChange}
        foreigner={foreigner}
        isLoading={isLoading}
      />
    </main>
  );
}
